import { useEffect, useState } from "react";
import { api, API } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { ChevronRight, ChevronLeft, Loader2, Sparkles } from "lucide-react";

const FALLBACK_ICON = {
  welcome: "🌱",
  log: "🎤",
  gamify: "🏆",
  sync: "⌚",
  start: "🚀",
};

export default function OnboardingWizard({ open, onClose, autoMarkComplete = true }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/client/onboarding")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const slides = data?.slides || [];
  const slide = slides[idx];
  const last = idx === slides.length - 1;

  const finish = async () => {
    if (autoMarkComplete) {
      try { await api.post("/client/onboarding/complete"); } catch {}
    }
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="onboarding-wizard">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        {/* Header — skip button */}
        <div className="flex items-center justify-between px-4 pt-3">
          <div className="flex gap-1.5" data-testid="onboarding-dots">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-[#FC9700]" : i < idx ? "w-1.5 bg-[#518688]" : "w-1.5 bg-slate-200"
                }`}
              />
            ))}
          </div>
          <button
            onClick={finish}
            data-testid="onboarding-skip"
            className="text-xs font-semibold text-slate-400 hover:text-slate-700 transition"
          >
            {t("onboardingTutorial.skip")}
          </button>
        </div>

        {loading ? (
          <div className="h-96 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#518688]" />
          </div>
        ) : slide ? (
          <>
            {/* Image */}
            <div className="relative aspect-square bg-gradient-to-br from-[#518688]/10 to-[#FC9700]/10 flex items-center justify-center overflow-hidden">
              {slide.image_url ? (
                <img
                  src={`${API.replace(/\/api$/, "")}${slide.image_url}`}
                  loading="eager"
                  decoding="async"
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-8xl drop-shadow-lg">{FALLBACK_ICON[slide.id] || "✨"}</div>
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            {/* Body */}
            <div className="p-5 space-y-2.5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FC9700]">
                {t(`onboardingTutorial.slides.${slide.id}.hint`) || slide.hint}
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 leading-tight" data-testid="onboarding-title">
                {t(`onboardingTutorial.slides.${slide.id}.title`) || slide.title}
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t(`onboardingTutorial.slides.${slide.id}.body`) || slide.body}
              </p>
            </div>

            {/* Nav footer */}
            <div className="flex items-center justify-between px-4 pb-4 pt-1">
              <button
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                data-testid="onboarding-prev"
                className={`inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold transition ${
                  idx === 0 ? "text-slate-300" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <ChevronLeft className="h-4 w-4" /> {t("onboardingTutorial.prev")}
              </button>
              {last ? (
                <button
                  onClick={finish}
                  data-testid="onboarding-finish"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#FC9700] hover:bg-[#E8951C] text-white text-sm font-bold px-5 py-2.5 shadow-md transition"
                >
                  <Sparkles className="h-4 w-4" /> {t("onboardingTutorial.start")}
                </button>
              ) : (
                <button
                  onClick={() => setIdx((i) => Math.min(slides.length - 1, i + 1))}
                  data-testid="onboarding-next"
                  className="inline-flex items-center gap-1 rounded-full bg-[#518688] hover:bg-[#3d6d6f] text-white text-sm font-semibold px-4 py-2 shadow-sm transition"
                >
                  {t("onboardingTutorial.next")} <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="h-96 flex items-center justify-center text-sm text-slate-500">
            {t("onboardingTutorial.loadError")}
          </div>
        )}
      </div>
    </div>
  );
}
